import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@dune/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Plus, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../../../api/queries/files.queries";
import { constructionSiteSubmissionQueryKeys } from "../../../api/query-keys/constructionSiteSubmission.query-keys";
import { UploadFilesModal } from "../../common/UploadFilesModal";
import { ReferenceDocumentModal } from "./ReferenceDocumentModal";

interface AddDocumentButtonProps {
	constructionSiteId: number;
	onUploadSuccess?: (fileId: string) => void;
}

export function AddDocumentButton({
	constructionSiteId,
	onUploadSuccess,
}: AddDocumentButtonProps) {
	const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const queryClient = useQueryClient();

	const uploadSingleFile = async (file: File) => {
		const signedUrl = await getSignedPutUrl({
			bucket: "private",
			contentType: file.type,
		});

		await uploadFileToS3(signedUrl.signedUrl, file);

		const registeredFile = await registerS3File({
			bucket: "private",
			key: signedUrl.key,
			name: file.name,
			mimeType: file.type,
			size: file.size,
		});

		onUploadSuccess?.(registeredFile.id);
	};

	const handleUpload = async (files: File[]) => {
		try {
			for (const file of files) {
				await uploadSingleFile(file);
			}

			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			});

			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.operation(constructionSiteId),
			});

			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});

			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.referenceableFiles(
						constructionSiteId,
					),
			});
		} catch (error) {
			console.error("Erreur lors de l'upload:", error);
			toast.error("Erreur lors de l'ajout du document");
		}
	};

	const handleUploadClick = () => {
		setDropdownOpen(false);
		setIsUploadModalOpen(true);
	};

	const handleReferenceClick = () => {
		setDropdownOpen(false);
		setIsReferenceModalOpen(true);
	};

	return (
		<>
			<DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
				<DropdownMenuTrigger asChild>
					<Button variant="blueChip" size="sm" className="h-7 rounded-full">
						<Plus className="w-3 h-3" />
						Ajouter
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start">
					<DropdownMenuItem
						onClick={handleReferenceClick}
						className="flex items-center gap-2"
					>
						<Link className="w-4 h-4" />
						<span>Référencer un document existant</span>
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={handleUploadClick}
						className="flex items-center gap-2"
					>
						<Upload className="w-4 h-4" />
						<span>Ajouter un fichier depuis l'ordinateur</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<UploadFilesModal
				open={isUploadModalOpen}
				onOpenChange={setIsUploadModalOpen}
				onUpload={handleUpload}
			/>

			<ReferenceDocumentModal
				isOpen={isReferenceModalOpen}
				onClose={() => setIsReferenceModalOpen(false)}
				constructionSiteId={constructionSiteId}
				onSelectFile={onUploadSuccess}
			/>
		</>
	);
}
